'use client'

import { createStyleContext } from '@/lib/create-style-context'
import { styled } from '@/styled-system/jsx'
import { drawer } from '@/styled-system/recipes'
import {
  Dialog as ArkDrawer,
  DialogOpenChangeDetails,
} from '@ark-ui/react/dialog'
import { Portal } from '@ark-ui/react/portal'
import {
  forwardRef,
  useCallback,
  type ComponentProps,
  type ElementRef,
} from 'react'

const { withProvider, withContext } = createStyleContext(drawer)

const StyledDrawer = withProvider(ArkDrawer.Root)

export interface DrawerProps extends ComponentProps<typeof StyledDrawer> {
  onOpen?: () => void
  onClose?: () => void
}

export const Drawer = (props: DrawerProps) => {
  const { onOpenChange, onOpen, onClose, ...rest } = props

  const handleOpenChange = useCallback(
    (details: DialogOpenChangeDetails) => {
      onOpenChange?.(details)

      if (details.open) {
        onOpen?.()
      } else {
        onClose?.()
      }
    },
    [onClose, onOpen, onOpenChange]
  )

  return (
    <StyledDrawer
      lazyMount
      unmountOnExit
      closeOnInteractOutside
      onOpenChange={handleOpenChange}
      {...rest}
    />
  )
}

const StyledBackdrop = withContext(styled(ArkDrawer.Backdrop), 'backdrop')
const StyledContent = withContext(styled(ArkDrawer.Content), 'content')
const StyledPositioner = withContext(styled(ArkDrawer.Positioner), 'positioner')

export interface DrawerContentProps
  extends ComponentProps<typeof StyledContent> {}

export const DrawerContent = forwardRef<
  ElementRef<typeof StyledContent>,
  DrawerContentProps
>((props, ref) => {
  return (
    <Portal>
      <StyledBackdrop />
      <StyledPositioner>
        <StyledContent ref={ref} {...props} />
      </StyledPositioner>
    </Portal>
  )
})

DrawerContent.displayName = 'DrawerContent'

export const DrawerCloseTrigger = withContext(
  styled(ArkDrawer.CloseTrigger),
  'closeTrigger'
)

export const DrawerDescription = withContext(
  styled(ArkDrawer.Description),
  'description'
)
export const DrawerTitle = withContext(styled(ArkDrawer.Title), 'title')
export const DrawerTrigger = withContext(styled(ArkDrawer.Trigger), 'trigger')

export interface DrawerCloseTriggerProps
  extends ComponentProps<typeof DrawerCloseTrigger> {}
export interface DrawerDescriptionProps
  extends ComponentProps<typeof DrawerDescription> {}
export interface DrawerTitleProps extends ComponentProps<typeof DrawerTitle> {}
export interface DrawerTriggerProps
  extends ComponentProps<typeof DrawerTrigger> {}
